import React from "react";
import { Box, Typography, Chip, Stack } from "@mui/material";
import PublisherComment from "./PublisherComment";
import ChipList from "../common/ChipList";
import { styled } from "@mui/material/styles";

const ContentBox = styled(Box)(({ theme }) => ({
  width: "100%",
  marginBottom: theme.spacing(1.5),
  padding: "10px 14px",
  background: "rgba(255,255,255,0.4)",
  backdropFilter: "blur(10px)",
  borderRadius: "10px",
  border: "1px solid rgba(226,232,240,0.3)",
  "& .publisher-comment": {
    fontSize: "13px",
    lineHeight: 1.5,
    color: "rgba(71,85,105,0.9)",
    fontWeight: 400,
  },
}));

const TagsBox = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  "& .MuiChip-root": {
    margin: theme.spacing(0.25),
    background: "linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.08) 100%)",
    color: "rgba(99,102,241,0.9)",
    border: "1px solid rgba(99,102,241,0.15)",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: 600,
    height: "24px",
    padding: "0 8px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    "&:hover": {
      background: "linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(139,92,246,0.15) 100%)",
      borderColor: "rgba(99,102,241,0.25)",
      transform: "translateY(-2px) scale(1.02)",
      boxShadow: "0 4px 16px rgba(99,102,241,0.2)",
      color: "#6366f1",
    },
    "&.highlighted": {
      background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
      color: "white",
      borderColor: "rgba(255,255,255,0.2)",
      boxShadow: "0 4px 16px rgba(99,102,241,0.3)",
      transform: "translateY(-1px) scale(1.05)",
    },
    "& .MuiChip-label": {
      padding: "0 4px",
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.3px",
    },
    "& .MuiChip-deleteIcon": {
      fontSize: "14px",
      color: "rgba(99,102,241,0.6)",
      "&:hover": {
        color: "#6366f1",
      }
    }
  },
}));

const CollectionsBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
}));

const PriceBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  padding: "10px 14px",
  background: "linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(34,197,94,0.1) 100%)",
  border: "1px solid rgba(16,185,129,0.2)",
  borderRadius: "10px",
  "& .price-text": {
    fontSize: "14px",
    fontWeight: 700,
    color: "rgba(16,185,129,0.9)",
  },
}));

const HomeCardBody = ({ data, searhTags, onMenuAction }) => {
  const hasContent = data.content || data.title;
  const hasTags = data.tags && data.tags.length > 0;
  const hasCollections = (data.spaces_list || data.spaces || data.collections) && 
    ((data.spaces_list && data.spaces_list.length > 0) || 
     (data.spaces && data.spaces.length > 0) || 
     (data.collections && data.collections.length > 0));
  const hasPrice = data.price || data.price_label;

  return (
    <Box>
      {hasContent && (
        <ContentBox>
          <PublisherComment 
            comment={data.title || data.content} 
            onClickDisabled 
          />
        </ContentBox>
      )}

      {/* Price Section */}
      {hasPrice && (
        <Box sx={{ mb: 1.5 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(16,185,129,0.8)',
              fontWeight: 600,
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 0.5,
              display: 'block'
            }}
          >
            Price
          </Typography>
          <PriceBox>
            <Typography className="price-text">
              {data.currency && data.currency !== "unknown" ? (
                `${data.price}${data.currency}`
              ) : (
                data.price_label
              )}
            </Typography>
          </PriceBox>
        </Box>
      )}

      {/* Tags Section - Only show heading when there are tags */}
      {hasTags && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 1,
            px: 0.5
          }}>
            <Box sx={{
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              mr: 1,
              boxShadow: '0 0 8px rgba(99,102,241,0.4)'
            }} />
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(99,102,241,0.9)',
                fontWeight: 700,
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.1) 100%)',
                padding: '2px 8px',
                borderRadius: '8px',
                border: '1px solid rgba(99,102,241,0.15)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)'
              }}
            >
              Tags
            </Typography>
          </Box>
          <TagsBox>
            <ChipList 
              list={data.tags} 
              highlightList={searhTags} 
              AllowChipOverflow 
            />
          </TagsBox>
        </Box>
      )}

      {/* Collections Section - Only show heading when there are collections */}
      {hasCollections && (
        <CollectionsBox>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 1,
            px: 0.5
          }}>
            <Box sx={{
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
              mr: 1,
              boxShadow: '0 0 8px rgba(139,92,246,0.4)'
            }} />
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(139,92,246,0.9)',
                fontWeight: 700,
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                background: 'linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(168,85,247,0.1) 100%)',
                padding: '2px 8px',
                borderRadius: '8px',
                border: '1px solid rgba(139,92,246,0.15)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)'
              }}
            >
              Collections
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: 'wrap',
              gap: 0.5
            }}
          >
            {(data.spaces_list || data.spaces || data.collections || []).map((space, index) => (
              <Chip
                key={space.id || space._id || index}
                label={space.name || space.title}
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onMenuAction) onMenuAction('spaceClick', space);
                }}
                sx={{
                  background: 'linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(168,85,247,0.08) 100%)',
                  color: 'rgba(139,92,246,0.9)',
                  border: '1px solid rgba(139,92,246,0.15)',
                  borderRadius: '12px',
                  fontSize: '11px',
                  height: '24px',
                  fontWeight: 600,
                  padding: '0 8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(168,85,247,0.15) 100%)',
                    borderColor: 'rgba(139,92,246,0.25)',
                    transform: 'translateY(-2px) scale(1.02)',
                    boxShadow: '0 4px 16px rgba(139,92,246,0.2)',
                    color: '#8b5cf6',
                  },
                  cursor: 'pointer',
                  '& .MuiChip-label': {
                    px: 1.5,
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.3px',
                  }
                }}
              />
            ))}
          </Stack>
        </CollectionsBox>
      )}
    </Box>
  );
};

export default HomeCardBody;
